import { ApiError } from "./errors";
import { postMessagesSend, postMessagesThreadRead, getMessagesThread, getMessagesTransaction } from "./messages";
import { postThreadsCreate, postThreadsArchive } from "./threads";
import { postTransactionExport, postTransactionArchive, getTransaction } from "./transactions";
import { getNotificationsUser, postNotificationsMarkRead } from "./notifications";
import { getAccessibleTransactions } from "./scope";
import { postClientEmailOptIn } from "./clients";
import { getAuditLogs } from "./audit";
import { postInquirySend, listOfficePeers } from "./inquiries";
import { listBetaFeedback, recordBetaFeedback } from "../betaFeedback";
import { queueSupportNote, readSupportQueue } from "../supportMail";

export { ApiError } from "./errors";
export { postMessagesSend, postMessagesThreadRead, getMessagesThread, getMessagesTransaction } from "./messages";
export { postThreadsCreate, postThreadsArchive } from "./threads";
export { postTransactionExport, postTransactionArchive, getTransaction } from "./transactions";
export { getNotificationsUser, postNotificationsMarkRead } from "./notifications";
export { resolveActor, canAccessTransaction, canAccessThread, listAccessibleTransactions } from "./access";
export { getAccessibleTransactions } from "./scope";
export { postClientEmailOptIn } from "./clients";
export { getAuditLogs, auditLabel, auditDetail } from "./audit";

function match(path, pattern) {
  const pathParts = path.replace(/\/+$/, "").split("/");
  const patternParts = pattern.split("/");
  if (pathParts.length !== patternParts.length) return null;
  const params = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    if (patternParts[i].startsWith(":")) {
      params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

/**
 * Swap-ready entry: later this becomes `fetch(path, { method, body })`.
 * POST /api/messages/send
 * POST /api/messages/thread/:thread_id/read
 * GET /api/messages/thread/:thread_id
 * GET /api/messages/transaction/:transaction_id
 * POST /api/threads/create
 * POST /api/threads/archive
 * GET /api/transactions
 * GET /api/transactions/:id
 * POST /api/transactions/:id/export
 * POST /api/transactions/:id/archive
 * GET /api/notifications/user/:user_id
 * POST /api/notifications/mark-read
 * POST /api/clients/email-opt-in
 * GET /api/audit
 * GET /api/transactions/:id/audit
 */
export async function api(method, path, body, session) {
  const verb = String(method || "GET").toUpperCase();
  const [route, queryString] = String(path || "").split("?");
  const query = Object.fromEntries(new URLSearchParams(queryString || ""));

  try {
    let params;
    if (verb === "POST" && route === "/api/messages/send") {
      return { ok: true, status: 200, data: postMessagesSend(body, session) };
    }
    params = match(route, "/api/messages/thread/:thread_id/read");
    if (verb === "POST" && params) {
      return { ok: true, status: 200, data: postMessagesThreadRead(params.thread_id, session) };
    }
    params = match(route, "/api/messages/thread/:thread_id");
    if (verb === "GET" && params) {
      return { ok: true, status: 200, data: getMessagesThread(params.thread_id, session) };
    }
    params = match(route, "/api/messages/transaction/:transaction_id");
    if (verb === "GET" && params) {
      return { ok: true, status: 200, data: getMessagesTransaction(params.transaction_id, session) };
    }
    if (verb === "POST" && route === "/api/threads/create") {
      return { ok: true, status: 200, data: postThreadsCreate(body, session) };
    }
    if (verb === "POST" && route === "/api/threads/archive") {
      return { ok: true, status: 200, data: postThreadsArchive(body, session) };
    }
    if (verb === "GET" && route === "/api/transactions") {
      return { ok: true, status: 200, data: getAccessibleTransactions(session) };
    }
    params = match(route, "/api/transactions/:id/export");
    if (verb === "POST" && params) {
      return { ok: true, status: 200, data: postTransactionExport(params.id, session, body) };
    }
    params = match(route, "/api/transactions/:id/archive");
    if (verb === "POST" && params) {
      return { ok: true, status: 200, data: postTransactionArchive(params.id, session, body) };
    }
    params = match(route, "/api/transactions/:id/audit");
    if (verb === "GET" && params) {
      return { ok: true, status: 200, data: getAuditLogs(session, { ...query, transaction_id: params.id }) };
    }
    params = match(route, "/api/transactions/:id");
    if (verb === "GET" && params) {
      return { ok: true, status: 200, data: getTransaction(params.id, session) };
    }
    params = match(route, "/api/notifications/user/:user_id");
    if (verb === "GET" && params) {
      return { ok: true, status: 200, data: getNotificationsUser(params.user_id, session) };
    }
    if (verb === "POST" && route === "/api/notifications/mark-read") {
      return { ok: true, status: 200, data: postNotificationsMarkRead(body, session) };
    }
    if (verb === "POST" && route === "/api/clients/email-opt-in") {
      return { ok: true, status: 200, data: postClientEmailOptIn(body, session) };
    }
    if (verb === "GET" && route === "/api/audit") {
      return { ok: true, status: 200, data: getAuditLogs(session, query) };
    }
    if (verb === "GET" && route === "/api/inquiries/peers") {
      return { ok: true, status: 200, data: listOfficePeers(session) };
    }
    if (verb === "POST" && route === "/api/inquiries/send") {
      return { ok: true, status: 200, data: postInquirySend(body, session) };
    }
    if (verb === "GET" && route === "/api/beta/feedback") {
      if (session?.role !== "admin") return { ok: false, status: 403, error: "Admin only" };
      return { ok: true, status: 200, data: listBetaFeedback() };
    }
    if (verb === "POST" && route === "/api/beta/feedback") {
      return { ok: true, status: 200, data: recordBetaFeedback({ session, message_text: body?.message_text }) };
    }
    if (verb === "POST" && route === "/api/support/send") {
      return { ok: true, status: 200, data: queueSupportNote(body || {}) };
    }
    if (verb === "GET" && route === "/api/support/inbox") {
      if (session?.role !== "admin") return { ok: false, status: 403, error: "Admin only" };
      return { ok: true, status: 200, data: readSupportQueue() };
    }
    return { ok: false, status: 404, error: "Unknown endpoint" };
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, status: error.status, error: error.message };
    }
    return { ok: false, status: 500, error: error.message || "Server error" };
  }
}
